import React, { useReducer, useEffect, useRef, useState, useMemo, useCallback } from "react";
import TraffickingRequestModal from "../../Modals/TraffickingRequestModal";
import MissingSiteIdsModal from "../../Modals/MissingSiteIdsModal";
import { getSiteContacts } from "../../../Flux/Actions/TraffickingActions";
import { checkTraffickingRequestValidDates } from "../../../Flux/Actions/PlacementActions";
import Tooltip, { TOOLTIP_POSITION } from "@acadia-ui/tooltip";
import Button, { BUTTON_DISPLAY, BUTTON_SIZE } from "@acadia-ui/button";
import WorkbookStore from "../../../Flux/Stores/WorkbookStore";
import TraffickingDataStore from "../../../Flux/Stores/TraffickingDataStore";
import { ToastNotification, showToast, TOAST_TYPE } from "@acadia-ui/toast";
import { draftHLISelected } from "../../../Helpers/ActionHelpers";

export default function useTrafficking({ refresh, campaignMetaData, campaignJiraTicketExists, selectedRows, setSelectedRows, gcmEnabled, dataTableApi }) {
	const traffickingModal = useRef(null);
	const missingSiteIdsModal = useRef(null);

	const [missingSiteIDs, setMissingSiteIDs] = useState([]);

	const [[clientTMEnabled, campaignTMEnabled], setTMEnabled] = useReducer(() => {
		return [
			// this comment exists to prevent eslint from making this unreadable.
			WorkbookStore.getIsTrafficEnabled(),
			WorkbookStore.getTrafficManagerEnabled()
		];
	}, [false, false]);

	const TMEnabled = useMemo(() => {
		const isPrisma = campaignMetaData.PlatformName == "Mediaocean (OX)/Prisma";
		return isPrisma ? campaignTMEnabled : clientTMEnabled;
	}, [clientTMEnabled, campaignTMEnabled]);

	const submitTraffickingSuccess = () => {
		refresh();
		showToast("workbookToasts", <ToastNotification type={TOAST_TYPE.SUCCESS} message="Traffic request submitted successfully." timed={5000} />);
	};

	const mappingSuccess = () => {
		refresh();
		showToast("workbookToasts", <ToastNotification type={TOAST_TYPE.SUCCESS} message="Item(s) successfully verified with GCM" timed={5000} />);
	};

	const mappingFail = () => {
		refresh();
	};

	useEffect(() => {
		WorkbookStore.on("fetchSyncStatusesFailure", setTMEnabled);
		WorkbookStore.on("updated_metaData", setTMEnabled);

		TraffickingDataStore.on("submit_trafficking_success", submitTraffickingSuccess);
		TraffickingDataStore.on("mapping_success", mappingSuccess);
		TraffickingDataStore.on("mapping_fail", mappingFail);

		setTMEnabled();

		return () => {
			WorkbookStore.removeListener("fetchSyncStatusesFailure", setTMEnabled);
			WorkbookStore.removeListener("updated_metaData", setTMEnabled);
			TraffickingDataStore.removeListener("submit_trafficking_success", submitTraffickingSuccess);
			TraffickingDataStore.removeListener("mapping_success", mappingSuccess);
			TraffickingDataStore.removeListener("mapping_fail", mappingFail);
		};
	}, []);

	const openTraffickingModal = useCallback(() => {
		setSelectedRows((selectedRows) => {
			if (!["Mediaocean (OX)/Prisma", "Google Campaign Manager"].includes(campaignMetaData.PlatformName)) {
				setTimeout(() => {
					traffickingModal?.current?.triggerOpenClose?.();
				}, 0);
				return selectedRows;
			}

			const placementIDs = [];
			const selectedPlacementPBs = {};

			selectedRows.forEach((placement) => {
				placementIDs.push(placement.PlacementID);
				selectedPlacementPBs[placement.PlacementID] = placement.InNameSite ? placement.InNameSite : placement.Partner;
			});
			if (campaignMetaData.PlatformName === "Google Campaign Manager") {
				checkTraffickingRequestValidDates(placementIDs).then((response) => {
					traffickingModal.current.setLiveDateRestrictions(response);
				});
				return selectedRows;
			}

			getSiteContacts({ PlacementID: placementIDs }).then((response) => {
				const newMissingSiteIDs = [];
				Object.keys(response).forEach((placementID) => {
					if (!response[placementID]?.GoogleSiteID) {
						if (selectedPlacementPBs[placementID] && !newMissingSiteIDs.includes(selectedPlacementPBs[placementID])) {
							newMissingSiteIDs.push(selectedPlacementPBs[placementID]);
						}
					}
				});
				if (newMissingSiteIDs.length !== 0) {
					setMissingSiteIDs(newMissingSiteIDs);
					setTimeout(() => {
						missingSiteIdsModal.current.triggerOpenClose();
					}, 0);
				} else {
					const placementIDs = [];
					selectedRows.forEach((placement) => {
						placementIDs.push(placement.PlacementID);
					});
					checkTraffickingRequestValidDates(placementIDs).then((response) => {
						traffickingModal.current.setLiveDateRestrictions(response);
					});
				}
			});
			return selectedRows;
		});
	}, [campaignMetaData, selectedRows, traffickingModal]);

	const Trigger = useCallback(() => {
		const selectedIds =
			dataTableApi?.current?.rowSelectionManager?.tree?.children?.loadedNodes
				?.filter(({ selectionState }) => selectionState === true)
				?.map(({ renderProps }) => renderProps.value) || [];
		const selectedRows = selectedIds?.map((id) => dataTableApi?.current?.dataProvider?.data?.find?.(({ UniqueID }) => UniqueID === id)) || [];
		const statusIsValid = (row) => (row ? row.TraffickingPlacementStatusID === "5" && (!gcmEnabled || (gcmEnabled && row.ExternalSystemStatusID == 3)) : false);
		const triggerDisabled = !(TMEnabled === true && selectedRows.every(statusIsValid)) || draftHLISelected(dataTableApi);
		const triggerHidden = !TMEnabled;

		if (!triggerHidden) {
			return (
				<Tooltip key="action-traffic-request" title="Start traffic request" position={TOOLTIP_POSITION.BOTTOM_START}>
					<Button
						// this comment exists to prevent eslint from making this unreadable.
						size={BUTTON_SIZE.SMALL}
						display={BUTTON_DISPLAY.ICON_BORDERED}
						icon="icon-play"
						onClick={openTraffickingModal}
						disabled={triggerDisabled}
						data-tracking-id-alt="open-traffic-request-modal"
					>
						Start traffic request
					</Button>
				</Tooltip>
			);
		}
		return <></>;
	}, [openTraffickingModal, gcmEnabled, TMEnabled, selectedRows]);

	const DisplayedComponent = useCallback(() => {
		return (
			<>
				<TraffickingRequestModal
					traffickingModal={(ref) => {
						traffickingModal.current = ref;
					}}
					selectedRows={selectedRows}
					campaignMetaData={campaignMetaData}
					hierarchyLevel="Placement"
					campaignJIRATicket={campaignJiraTicketExists}
				/>
				<MissingSiteIdsModal
					missingSiteIdsModal={(ref) => {
						missingSiteIdsModal.current = ref;
					}}
					missingSiteIDs={missingSiteIDs}
				/>
			</>
		);
	}, [selectedRows, missingSiteIDs, campaignMetaData, campaignJiraTicketExists]);

	return useMemo(
		() => [Trigger, DisplayedComponent, { campaignTMEnabled, clientTMEnabled, TMEnabled }],
		[Trigger, DisplayedComponent, campaignTMEnabled, clientTMEnabled, TMEnabled]
	);
}
